import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { getUserStats, getUsers, getStripeLiveStats } from '../api/userAccounts';
import type { UserAccountStats, UserAccountListItem, StripeLiveStats } from '../types/userAccount';
import styles from './AccountsOverview.module.css';

// Plan pricing — sourced from BYOKWA frontend Account.jsx:
//   standardMonthly = 9.99 (USD/month)
//   standardAnnual  = 6.99 (USD/month, billed annually) → $83.88/year actual charge
// If pricing changes (promos, grandfathered rates, regional pricing), update here.
const PLAN_PRICES = {
  monthlyStandard: 9.99,
  yearlyStandard: 83.88,
} as const;

// Calendar buckets shown on the chart. We display 13 because a strict
// rolling 365-day window naturally spans current-calendar-month plus the
// same calendar month next year (e.g. May 2026 through May 2027) — a sub
// started today lands its next renewal in bucket 12, so changes are visible
// immediately rather than disappearing past a 12-bucket horizon.
const FORECAST_BUCKETS = 13;
// Used for "average per month" — we capture exactly 12 months of revenue,
// just spread across 13 partial-or-full calendar bars.
const FORECAST_MONTHS = 12;

interface ForwardRevenueBucket {
  monthIndex: number;
  date: Date;
  label: string;
  monthlyRevenue: number;
  yearlyRevenue: number;
  total: number;
  monthlyCount: number;
  yearlyCount: number;
}

function formatLastUpdated(date: Date | null): string {
  if (!date) return 'Never';
  const now = new Date();
  const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diffSec < 5) return 'Just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  return `${Math.floor(diffMin / 60)}h ago`;
}

function formatCurrency(amount: number): string {
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

// Compact currency for narrow bar labels: $1,277 -> $1.3K, $12,400 -> $12K,
// $1,200,000 -> $1.2M. Used only in tight bar-chart label slots; the tooltip
// (title attribute) on each bar still shows the full-precision value.
function formatCurrencyCompact(amount: number): string {
  if (amount === 0) return '$0';
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
  });
}

interface ForecastAccumulators {
  buckets: ForwardRevenueBucket[];
  next30Days: number;
  next90Days: number;
  total365Days: number;
}

// Project forward billing events for a single subscription, depositing
// revenue into both the calendar-month buckets (for the chart) and the
// rolling day-windows (for the headline cards).
//
// Approximation: walks renewal dates forward from `start_date` by the plan's
// interval (1 month or 12 months). Stripe-side dunning, proration, and pause
// states are ignored — this is a directional view, not a ledger.
function projectSubscriptionRenewals(
  user: UserAccountListItem,
  acc: ForecastAccumulators,
  now: Date,
  cutoff30: Date,
  cutoff90: Date,
  cutoff365: Date,
): void {
  const sub = user.subscription;
  if (!sub) return;
  // Canceled subs (even those still in a paid period) generate no future
  // payments — Stripe stops billing them at the period end.
  if (!sub.active || sub.status !== 'active') return;
  // Lifetime is one-time; no recurring revenue to project.
  if (sub.plan_name === 'Lifetime Plan') return;

  const isMonthly = sub.plan_name === 'Monthly Standard';
  const isYearly = sub.plan_name === 'Yearly Standard';
  if (!isMonthly && !isYearly) return;

  const intervalMonths = isMonthly ? 1 : 12;
  const price = isMonthly ? PLAN_PRICES.monthlyStandard : PLAN_PRICES.yearlyStandard;

  const startDate = new Date(sub.start_date);
  if (isNaN(startDate.getTime())) return;

  const renewalDate = new Date(startDate);
  // Advance to the first renewal strictly after "now". Note: setMonth handles
  // overflow (e.g., Jan 31 + 1 month → Mar 3), which is fine at month-bucket
  // resolution.
  while (renewalDate <= now) {
    renewalDate.setMonth(renewalDate.getMonth() + intervalMonths);
  }

  // Walk forward, depositing revenue into each renewal's bucket and the
  // rolling-day accumulators it falls inside.
  while (true) {
    // Strict 365-day cutoff anchors the rolling window — bucket 12 may end up
    // partially populated, which is correct.
    if (renewalDate > cutoff365) break;

    const monthsAhead =
      (renewalDate.getFullYear() - now.getFullYear()) * 12 +
      (renewalDate.getMonth() - now.getMonth());
    if (monthsAhead < 0 || monthsAhead >= FORECAST_BUCKETS) break;

    const bucket = acc.buckets[monthsAhead];
    if (isMonthly) {
      bucket.monthlyRevenue += price;
      bucket.monthlyCount += 1;
    } else {
      bucket.yearlyRevenue += price;
      bucket.yearlyCount += 1;
    }
    bucket.total += price;

    acc.total365Days += price;
    if (renewalDate <= cutoff30) acc.next30Days += price;
    if (renewalDate <= cutoff90) acc.next90Days += price;

    renewalDate.setMonth(renewalDate.getMonth() + intervalMonths);
  }
}

// Page through every subscriber across all plan types and statuses, deduped
// by user id. One fetch powers the forward forecast, the historical signups
// chart, and the trend chart.
//
// Backend `plan` filters require `subscription__active=True`, so a separate
// `status=canceled` pass is needed to surface canceled subs. Backend caps
// page_size at 100.
async function fetchAllSubscribers(): Promise<UserAccountListItem[]> {
  const all: UserAccountListItem[] = [];
  const seen = new Set<number>();
  const PAGE_SIZE = 100;
  const MAX_PAGES = 100; // 10k users per filter pass

  type FetchPass =
    | { plan: 'monthly' | 'yearly' | 'lifetime' }
    | { status: 'canceled' };

  const passes: FetchPass[] = [
    { plan: 'monthly' },
    { plan: 'yearly' },
    { plan: 'lifetime' },
    { status: 'canceled' },
  ];

  for (const filter of passes) {
    let page = 1;
    while (page <= MAX_PAGES) {
      const data = await getUsers({ ...filter, page, page_size: PAGE_SIZE });
      for (const user of data.results) {
        if (!seen.has(user.id)) {
          seen.add(user.id);
          all.push(user);
        }
      }
      if (!data.next) break;
      page += 1;
    }
  }

  return all;
}

type HistoricalRange = '6mo' | '12mo' | '24mo' | 'all';

export default function AccountsOverview() {
  const [stats, setStats] = useState<UserAccountStats | null>(null);
  const [stripeLive, setStripeLive] = useState<StripeLiveStats | null>(null);
  const [stripeLiveError, setStripeLiveError] = useState<string | null>(null);
  const [allSubs, setAllSubs] = useState<UserAccountListItem[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSubs, setIsLoadingSubs] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subsError, setSubsError] = useState<string | null>(null);
  const [lastFetch, setLastFetch] = useState<Date | null>(null);
  const [historicalRange, setHistoricalRange] = useState<HistoricalRange>('12mo');
  // Selected bar in each chart (for tap-to-reveal on mobile, also works on
  // desktop). null = nothing selected; readout shows a hint instead.
  const [selectedForecastBucket, setSelectedForecastBucket] = useState<number | null>(null);
  const [selectedHistoricalBucket, setSelectedHistoricalBucket] = useState<number | null>(null);

  // Deselect the historical bar when the time-range toggle changes — bucket
  // indices map to different months across ranges, so the prior selection
  // would silently point at a different bar.
  useEffect(() => {
    setSelectedHistoricalBucket(null);
  }, [historicalRange]);

  const loadStats = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getUserStats();
      setStats(data);
      setLastFetch(new Date());
    } catch {
      setError('Failed to load stats');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Pulls counts directly from Stripe. Failures here never block the rest of
  // the dashboard — we just fall back to the local-DB numbers from getUserStats.
  const loadStripeLive = useCallback(async () => {
    setStripeLiveError(null);
    try {
      const data = await getStripeLiveStats();
      setStripeLive(data);
    } catch {
      setStripeLiveError('Could not reach Stripe — showing local DB counts as fallback.');
      setStripeLive(null);
    }
  }, []);

  const loadSubs = useCallback(async () => {
    setIsLoadingSubs(true);
    setSubsError(null);
    try {
      const subs = await fetchAllSubscribers();
      setAllSubs(subs);
    } catch {
      setSubsError('Failed to load subscription data');
    } finally {
      setIsLoadingSubs(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadStats(), loadStripeLive(), loadSubs()]);
  }, [loadStats, loadStripeLive, loadSubs]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  // Headline counts for "Active Subscriptions by Plan". Stripe is the source
  // of truth for monthly/yearly when available; lifetime always comes from
  // local (Stripe doesn't track one-time payments as "active" subscriptions).
  // If Stripe is unreachable, fall back to local for monthly/yearly too.
  const monthlyActiveDisplay = stripeLive?.monthly_active ?? stats?.active_subscriptions.monthly ?? 0;
  const yearlyActiveDisplay = stripeLive?.yearly_active ?? stats?.active_subscriptions.yearly ?? 0;
  const lifetimeActiveDisplay = stats?.active_subscriptions.lifetime ?? 0;
  // Recurring-only total (used for the top "Active Subscriptions" stat card —
  // lifetime is a one-time purchase, not a subscription, so it doesn't belong
  // in that count). The all-plans total below is still used for the percentage
  // bars on the per-plan breakdown.
  const totalRecurringActive = monthlyActiveDisplay + yearlyActiveDisplay;
  const totalActive = totalRecurringActive + lifetimeActiveDisplay;
  const monthlyLocal = stats?.active_subscriptions.monthly ?? 0;
  const yearlyLocal = stats?.active_subscriptions.yearly ?? 0;
  const stripeIsAuthoritative = stripeLive !== null;
  const stripeIsStale = stripeLive?.stale === true;
  const stripeAgeSeconds = stripeLive?.cache_age_seconds;

  // "Stripe-implied" annual revenue sanity check, computed from the live
  // Stripe sub counts × sticker prices. Doesn't account for promos or
  // grandfathered rates, so this is a CEILING estimate. We only compute it
  // when Stripe data is available (otherwise both numbers come from the same
  // source and the comparison is meaningless).
  const stripeImpliedAnnual = stripeIsAuthoritative
    ? monthlyActiveDisplay * PLAN_PRICES.monthlyStandard * 12
      + yearlyActiveDisplay * PLAN_PRICES.yearlyStandard
    : null;

  // Compute rolling 12-month forward revenue projection.
  // Headline cards (Next 30/90/365 days) use exact day-based windows so they
  // react immediately when a sub is added today. The chart spans 13 calendar
  // buckets so a yearly renewal exactly 12 months out is still visible.
  const forecast = useMemo(() => {
    if (!allSubs) return null;

    const now = new Date();
    const cutoff30 = new Date(now);
    cutoff30.setDate(cutoff30.getDate() + 30);
    const cutoff90 = new Date(now);
    cutoff90.setDate(cutoff90.getDate() + 90);
    const cutoff365 = new Date(now);
    cutoff365.setDate(cutoff365.getDate() + 365);

    const buckets: ForwardRevenueBucket[] = [];
    for (let i = 0; i < FORECAST_BUCKETS; i += 1) {
      const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
      buckets.push({
        monthIndex: i,
        date,
        label: date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        monthlyRevenue: 0,
        yearlyRevenue: 0,
        total: 0,
        monthlyCount: 0,
        yearlyCount: 0,
      });
    }

    const acc: ForecastAccumulators = {
      buckets,
      next30Days: 0,
      next90Days: 0,
      total365Days: 0,
    };

    let projectableSubCount = 0;
    let monthlySubCount = 0;
    let yearlySubCount = 0;
    for (const user of allSubs) {
      const sub = user.subscription;
      if (!sub || !sub.active || sub.status !== 'active') continue;
      if (sub.plan_name === 'Lifetime Plan') continue;
      projectableSubCount += 1;
      if (sub.plan_name === 'Monthly Standard') monthlySubCount += 1;
      if (sub.plan_name === 'Yearly Standard') yearlySubCount += 1;
      projectSubscriptionRenewals(user, acc, now, cutoff30, cutoff90, cutoff365);
    }

    const total12Mo = acc.total365Days;
    const avgPerMonth = total12Mo / FORECAST_MONTHS;
    const peakBucket = buckets.reduce((max, b) => (b.total > max.total ? b : max), buckets[0]);

    return {
      buckets,
      total12Mo,
      avgPerMonth,
      next30Days: acc.next30Days,
      next90Days: acc.next90Days,
      peakBucket,
      projectableSubCount,
      monthlySubCount,
      yearlySubCount,
    };
  }, [allSubs]);

  const maxBucketTotal = forecast
    ? Math.max(...forecast.buckets.map((b) => b.total), 1)
    : 1;

  // Compute historical signups by month, segmented by plan type.
  // Uses each subscription's start_date — note this represents the user's
  // CURRENT subscription record, so plan switches (e.g. Monthly → Yearly)
  // count as a single signup at the latest plan's start date, not as two
  // separate events.
  const historical = useMemo(() => {
    if (!allSubs) return null;

    const now = new Date();
    let monthsBack: number;
    if (historicalRange === '6mo') monthsBack = 6;
    else if (historicalRange === '12mo') monthsBack = 12;
    else if (historicalRange === '24mo') monthsBack = 24;
    else monthsBack = 60; // 'all' — cap at 5 years to keep chart readable

    // Initialize buckets oldest → newest (left to right on chart).
    // Recurring plans only — lifetime is a one-time product and is shown
    // in its own dedicated section further down the page.
    const buckets: Array<{
      monthIndex: number;
      date: Date;
      label: string;
      monthlyCount: number;
      yearlyCount: number;
      total: number;
    }> = [];
    for (let i = monthsBack - 1; i >= 0; i -= 1) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      buckets.push({
        monthIndex: monthsBack - 1 - i,
        date,
        label: date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        monthlyCount: 0,
        yearlyCount: 0,
        total: 0,
      });
    }

    let totalCounted = 0;
    for (const user of allSubs) {
      const sub = user.subscription;
      if (!sub) continue;
      const startDate = new Date(sub.start_date);
      if (isNaN(startDate.getTime())) continue;

      const monthsAgo =
        (now.getFullYear() - startDate.getFullYear()) * 12 +
        (now.getMonth() - startDate.getMonth());
      if (monthsAgo < 0 || monthsAgo >= monthsBack) continue;

      const bucketIdx = monthsBack - 1 - monthsAgo;
      const bucket = buckets[bucketIdx];
      if (sub.plan_name === 'Monthly Standard') bucket.monthlyCount += 1;
      else if (sub.plan_name === 'Yearly Standard') bucket.yearlyCount += 1;
      else continue; // skip Lifetime + any unknown plan
      bucket.total += 1;
      totalCounted += 1;
    }

    const peakBucket = buckets.reduce((max, b) => (b.total > max.total ? b : max), buckets[0]);
    const monthsWithData = buckets.filter((b) => b.total > 0).length;
    const avgPerMonth = monthsWithData > 0 ? totalCounted / monthsWithData : 0;

    return {
      buckets,
      monthsBack,
      totalCounted,
      avgPerMonth,
      peakBucket,
    };
  }, [allSubs, historicalRange]);

  const maxHistoricalTotal = historical
    ? Math.max(...historical.buckets.map((b) => b.total), 1)
    : 1;

  // Monthly time-series of (a) active subs per plan at the end of each month
  // and (b) cancellations within each month, segmented by plan.
  //
  // Snapshot semantics: for each calendar month in the window, count subs
  // where start_date <= snapshotDate AND (canceled_at IS NULL OR canceled_at > snapshotDate).
  // The current month uses `now` instead of month-end for snapshot accuracy.
  //
  // Caveat: the backend serializer returns each user's most-recent subscription
  // only. Users who upgraded plans (e.g. Monthly → Yearly) lose their original
  // sub history, so the trend reflects "current sub of each user as of T",
  // not a true historical replay.
  const trend = useMemo(() => {
    if (!allSubs) return null;

    const now = new Date();
    let monthsBack: number;
    if (historicalRange === '6mo') monthsBack = 6;
    else if (historicalRange === '12mo') monthsBack = 12;
    else if (historicalRange === '24mo') monthsBack = 24;
    else monthsBack = 60;

    const buckets: Array<{
      monthIndex: number;
      monthStart: Date;
      monthEnd: Date;
      snapshotDate: Date;
      label: string;
      active: { monthly: number; yearly: number; lifetime: number };
      canceled: { monthly: number; yearly: number; lifetime: number };
    }> = [];

    for (let i = monthsBack - 1; i >= 0; i -= 1) {
      const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1);
      // Last instant of the month (day 0 of next month at 23:59:59).
      const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0, 23, 59, 59, 999);
      const snapshotDate = monthEnd > now ? now : monthEnd;
      buckets.push({
        monthIndex: monthsBack - 1 - i,
        monthStart,
        monthEnd,
        snapshotDate,
        label: monthEnd.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        active: { monthly: 0, yearly: 0, lifetime: 0 },
        canceled: { monthly: 0, yearly: 0, lifetime: 0 },
      });
    }

    for (const user of allSubs) {
      const sub = user.subscription;
      if (!sub) continue;
      const start = new Date(sub.start_date);
      if (isNaN(start.getTime())) continue;
      const cancelDate = sub.canceled_at ? new Date(sub.canceled_at) : null;

      let planKey: 'monthly' | 'yearly' | 'lifetime' | null = null;
      if (sub.plan_name === 'Monthly Standard') planKey = 'monthly';
      else if (sub.plan_name === 'Yearly Standard') planKey = 'yearly';
      else if (sub.plan_name === 'Lifetime Plan') planKey = 'lifetime';
      if (!planKey) continue;

      for (const bucket of buckets) {
        if (start <= bucket.snapshotDate && (!cancelDate || cancelDate > bucket.snapshotDate)) {
          bucket.active[planKey] += 1;
        }
        if (cancelDate && cancelDate >= bucket.monthStart && cancelDate <= bucket.monthEnd) {
          bucket.canceled[planKey] += 1;
        }
      }
    }

    // Anchor the most recent bucket to the same source the headline cards use,
    // so the chart's last data point matches "Active Subscriptions by Plan"
    // exactly. Historical buckets stay as-reconstructed from each user's
    // most-recent sub record (best we can do without a historical replay).
    //
    // Priority for monthly/yearly: Stripe-live > local DB stats > date-based
    // reconstruction. Lifetime always uses local stats (Stripe doesn't track
    // one-time payments as recurring subscriptions).
    if (buckets.length > 0) {
      const last = buckets[buckets.length - 1];
      const monthlyAnchor = stripeLive?.monthly_active ?? stats?.active_subscriptions.monthly;
      const yearlyAnchor = stripeLive?.yearly_active ?? stats?.active_subscriptions.yearly;
      const lifetimeAnchor = stats?.active_subscriptions.lifetime;
      if (typeof monthlyAnchor === 'number') last.active.monthly = monthlyAnchor;
      if (typeof yearlyAnchor === 'number') last.active.yearly = yearlyAnchor;
      if (typeof lifetimeAnchor === 'number') last.active.lifetime = lifetimeAnchor;
    }

    // Recurring-only Y-axis scaling for the subscription chart. Lifetime gets
    // its own section below, so we don't want lifetime's much-larger values
    // squashing the recurring lines into the bottom of the plot area.
    const maxActive = Math.max(
      ...buckets.map((b) => Math.max(b.active.monthly, b.active.yearly)),
      1,
    );
    const maxCanceled = Math.max(
      ...buckets.map((b) => b.canceled.monthly + b.canceled.yearly),
      1,
    );
    const totalCanceledInWindow = buckets.reduce(
      (sum, b) => sum + b.canceled.monthly + b.canceled.yearly,
      0,
    );
    const latest = buckets[buckets.length - 1];
    const earliest = buckets[0];
    const netChange =
      (latest.active.monthly + latest.active.yearly) -
      (earliest.active.monthly + earliest.active.yearly);

    // --- Lifetime-specific stats (one-time product, different metrics) ---
    const lifetimeMaxActive = Math.max(...buckets.map((b) => b.active.lifetime), 1);
    const lifetimeNewByBucket = buckets.map((b, i) => {
      // "New purchases" = month-over-month delta in the cumulative count.
      // For the first bucket we can't compute a delta (no prior reference),
      // so we report 0 to avoid a misleading spike.
      if (i === 0) return 0;
      const delta = b.active.lifetime - buckets[i - 1].active.lifetime;
      return delta > 0 ? delta : 0;
    });
    const lifetimeMaxNew = Math.max(...lifetimeNewByBucket, 1);
    const lifetimeNewInWindow = lifetimeNewByBucket.reduce((s, n) => s + n, 0);
    const lifetimeRefundsInWindow = buckets.reduce((s, b) => s + b.canceled.lifetime, 0);
    let lifetimeBestMonth: { label: string; count: number } | null = null;
    for (let i = 0; i < buckets.length; i += 1) {
      const n = lifetimeNewByBucket[i];
      if (!lifetimeBestMonth || n > lifetimeBestMonth.count) {
        lifetimeBestMonth = { label: buckets[i].label, count: n };
      }
    }

    return {
      buckets,
      monthsBack,
      maxActive,
      maxCanceled,
      totalCanceledInWindow,
      netChange,
      lifetimeMaxActive,
      lifetimeNewByBucket,
      lifetimeMaxNew,
      lifetimeNewInWindow,
      lifetimeRefundsInWindow,
      lifetimeBestMonth,
    };
  }, [allSubs, historicalRange, stripeLive, stats]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Accounts Overview</h1>
          <p className={styles.subtitle}>User and subscription statistics</p>
        </div>
        <div className={styles.headerActions}>
          <span className={styles.lastUpdated}>Updated {formatLastUpdated(lastFetch)}</span>
          <button className={styles.refreshButton} onClick={refreshAll} disabled={isLoading || isLoadingSubs}>
            <span className={`${styles.refreshIcon} ${isLoading || isLoadingSubs ? styles.spinning : ''}`}>↻</span>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className={styles.error}>
          {error}
          <button className={styles.retryButton} onClick={loadStats}>Retry</button>
        </div>
      )}

      {isLoading && !stats ? (
        <div className={styles.loading}>Loading stats...</div>
      ) : stats && (
        <>
          <div className={styles.grid}>
            <div className={styles.card}>
              <div className={styles.cardLabel}>Total Users</div>
              <div className={styles.cardValue}>{stats.total_users.toLocaleString()}</div>
            </div>
            <div className={`${styles.card} ${styles.cardBlue}`}>
              <div className={styles.cardLabel}>Active Trials</div>
              <div className={styles.cardValue}>{stats.active_trials.toLocaleString()}</div>
            </div>
            <div className={`${styles.card} ${styles.cardGreen}`}>
              <div className={styles.cardLabel}>Active Subscriptions</div>
              <div className={styles.cardValue}>{totalRecurringActive.toLocaleString()}</div>
              <div className={styles.cardSubNote}>Recurring only — lifetime excluded</div>
            </div>
            <div className={`${styles.card} ${styles.cardYellow}`}>
              <div className={styles.cardLabel}>Expired Trials (No Sub)</div>
              <div className={styles.cardValue}>{stats.expired_trials_no_subscription.toLocaleString()}</div>
            </div>
            <div className={`${styles.card} ${styles.cardRed}`}>
              <div className={styles.cardLabel}>Canceled Subscriptions</div>
              <div className={styles.cardValue}>{stats.canceled_subscriptions.toLocaleString()}</div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardLabel}>Users with Devices</div>
              <div className={styles.cardValue}>{stats.users_with_devices.toLocaleString()}</div>
            </div>
          </div>

          <div className={styles.sectionHeaderRow}>
            <h2 className={styles.sectionTitle}>Active Subscriptions by Plan</h2>
            {stripeIsAuthoritative ? (
              <span
                className={stripeIsStale ? styles.sourceBadgeStale : styles.sourceBadgeLive}
                title={stripeLive?.note}
              >
                {stripeIsStale ? 'Stripe • cached' : 'Live from Stripe'}
                {stripeAgeSeconds !== null && stripeAgeSeconds !== undefined && stripeAgeSeconds > 0 && (
                  <> &middot; {stripeAgeSeconds}s ago</>
                )}
              </span>
            ) : (
              <span className={styles.sourceBadgeFallback} title={stripeLiveError ?? undefined}>
                Local DB (Stripe unreachable)
              </span>
            )}
          </div>
          {stripeLiveError && (
            <p className={styles.sourceFallbackNote}>{stripeLiveError}</p>
          )}
          <div className={styles.grid}>
            <div className={styles.planCard}>
              <div className={styles.planName}>Monthly Standard</div>
              <div className={styles.planCount}>{monthlyActiveDisplay.toLocaleString()}</div>
              {stripeIsAuthoritative && monthlyActiveDisplay !== monthlyLocal && (
                <div className={styles.planSubNote}>
                  Local DB has {monthlyLocal.toLocaleString()} — Stripe is authoritative
                </div>
              )}
              {totalActive > 0 && (
                <div className={styles.planBar}>
                  <div className={styles.planBarFill} style={{ width: `${(monthlyActiveDisplay / totalActive) * 100}%` }} />
                </div>
              )}
            </div>
            <div className={styles.planCard}>
              <div className={styles.planName}>Yearly Standard</div>
              <div className={styles.planCount}>{yearlyActiveDisplay.toLocaleString()}</div>
              {stripeIsAuthoritative && yearlyActiveDisplay !== yearlyLocal && (
                <div className={styles.planSubNote}>
                  Local DB has {yearlyLocal.toLocaleString()} — Stripe is authoritative
                </div>
              )}
              {totalActive > 0 && (
                <div className={styles.planBar}>
                  <div className={`${styles.planBarFill} ${styles.planBarYearly}`} style={{ width: `${(yearlyActiveDisplay / totalActive) * 100}%` }} />
                </div>
              )}
            </div>
            <div className={styles.planCard}>
              <div className={styles.planName}>Lifetime Plan</div>
              <div className={styles.planCount}>{lifetimeActiveDisplay.toLocaleString()}</div>
              <div className={styles.planSubNote}>Source: Local DB (one-time payment, not tracked as active sub by Stripe)</div>
              {totalActive > 0 && (
                <div className={styles.planBar}>
                  <div className={`${styles.planBarFill} ${styles.planBarLifetime}`} style={{ width: `${(lifetimeActiveDisplay / totalActive) * 100}%` }} />
                </div>
              )}
            </div>
          </div>

          <h2 className={styles.sectionTitle}>Recent Activity (Last 7 Days)</h2>
          <div className={styles.grid}>
            <div className={`${styles.card} ${styles.cardGreen}`}>
              <div className={styles.cardLabel}>New Signups</div>
              <div className={styles.cardValue}>{stats.recent_signups_7d.toLocaleString()}</div>
            </div>
            <div className={`${styles.card} ${styles.cardRed}`}>
              <div className={styles.cardLabel}>Cancellations</div>
              <div className={styles.cardValue}>{stats.recent_cancellations_7d.toLocaleString()}</div>
            </div>
            <div className={`${styles.card} ${styles.cardPurple}`}>
              <div className={styles.cardLabel}>Lifetime Revenue</div>
              <div className={styles.cardValue}>${Number(stats.total_revenue_lifetime).toLocaleString()}</div>
            </div>
          </div>

          <div className={styles.forecastHeader}>
            <h2 className={styles.sectionTitle}>Expected Recurring Revenue (Next 12 Months)</h2>
            <p className={styles.forecastCaveat}>
              Rolling 365-day forecast from today, projected from each active subscription&apos;s
              start date and plan cadence. Excludes lifetime and canceled subscriptions.
              Assumes no further churn. Chart shows 13 calendar bars — the first and last
              are partial months; together they span exactly 365 days.
            </p>
          </div>

          {subsError && (
            <div className={styles.error}>
              {subsError}
              <button className={styles.retryButton} onClick={loadSubs}>Retry</button>
            </div>
          )}

          {isLoadingSubs && !forecast ? (
            <div className={styles.loading}>Loading subscription data...</div>
          ) : forecast && (
            <>
              <div className={styles.grid}>
                <div className={`${styles.card} ${styles.cardGreen}`}>
                  <div className={styles.cardLabel}>Next 12 Months</div>
                  <div className={styles.cardValue}>{formatCurrency(forecast.total12Mo)}</div>
                </div>
                <div className={`${styles.card} ${styles.cardBlue}`}>
                  <div className={styles.cardLabel}>Avg / Month</div>
                  <div className={styles.cardValue}>{formatCurrency(forecast.avgPerMonth)}</div>
                </div>
                <div className={styles.card}>
                  <div className={styles.cardLabel}>Next 30 Days</div>
                  <div className={styles.cardValue}>{formatCurrency(forecast.next30Days)}</div>
                </div>
                <div className={styles.card}>
                  <div className={styles.cardLabel}>Next 90 Days</div>
                  <div className={styles.cardValue}>{formatCurrency(forecast.next90Days)}</div>
                </div>
              </div>

              {stripeImpliedAnnual !== null && (
                <p className={styles.sanityCheckNote}>
                  <strong>Sanity check vs. Stripe:</strong> at sticker prices
                  ({monthlyActiveDisplay.toLocaleString()} monthly &times; ${PLAN_PRICES.monthlyStandard}/mo &times; 12
                  {' + '}
                  {yearlyActiveDisplay.toLocaleString()} yearly &times; ${PLAN_PRICES.yearlyStandard}/yr),
                  Stripe-implied annual revenue is <strong>{formatCurrency(stripeImpliedAnnual)}</strong>.
                  {forecast.total12Mo > 0 && (() => {
                    const gapPct = ((stripeImpliedAnnual - forecast.total12Mo) / stripeImpliedAnnual) * 100;
                    if (Math.abs(gapPct) < 1) {
                      return <> Local forecast is within 1% — looking good.</>;
                    }
                    return (
                      <> Local forecast is {gapPct >= 0 ? `${gapPct.toFixed(1)}% under` : `${Math.abs(gapPct).toFixed(1)}% over`} —
                        likely due to {gapPct >= 0 ? 'a few' : 'extra'} active subs not perfectly synced with Stripe,
                        plus any promo / grandfathered pricing not reflected at sticker rates.</>
                    );
                  })()}
                </p>
              )}

              <div className={styles.chartCard}>
                <div className={styles.chartLegend}>
                  <span className={styles.legendItem}>
                    <span className={styles.legendDot} style={{ background: '#3b82f6' }} />
                    Monthly Standard
                  </span>
                  <span className={styles.legendItem}>
                    <span className={styles.legendDot} style={{ background: '#22c55e' }} />
                    Yearly Standard
                  </span>
                  <span className={styles.legendNote}>
                    {forecast.projectableSubCount.toLocaleString()} active recurring subs
                    {' '}({forecast.monthlySubCount.toLocaleString()} monthly · {forecast.yearlySubCount.toLocaleString()} yearly)
                  </span>
                </div>

                {/* Tap-to-reveal readout — shows the selected bucket's full
                    breakdown. Critical on mobile where bar values are too
                    cramped to read directly. Reserves a fixed height so the
                    chart layout doesn't shift on selection. */}
                <div
                  className={styles.chartReadout}
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {(() => {
                    if (selectedForecastBucket === null) {
                      return (
                        <span className={styles.chartReadoutHint}>
                          Tap a bar to see its amount
                        </span>
                      );
                    }
                    const b = forecast.buckets[selectedForecastBucket];
                    if (!b) return null;
                    const isPartial = b.monthIndex === 0 || b.monthIndex === FORECAST_BUCKETS - 1;
                    return (
                      <>
                        <strong>{b.label}{isPartial ? ' (partial)' : ''}:</strong>
                        {' '}{formatCurrency(b.total)}
                        <span className={styles.chartReadoutBreakdown}>
                          {' — Monthly '}{formatCurrency(b.monthlyRevenue)}
                          {' ('}{b.monthlyCount} renewals{')'}
                          {' · Yearly '}{formatCurrency(b.yearlyRevenue)}
                          {' ('}{b.yearlyCount} renewals{')'}
                        </span>
                      </>
                    );
                  })()}
                </div>

                {forecast.projectableSubCount === 0 ? (
                  <div className={styles.emptyChart}>
                    No active recurring subscriptions to project.
                  </div>
                ) : (
                  <div
                    className={styles.chart}
                    onClick={(e) => {
                      // Tapping the chart background (not a bar) deselects.
                      if (e.target === e.currentTarget) setSelectedForecastBucket(null);
                    }}
                  >
                    {forecast.buckets.map((bucket, idx) => {
                      const heightPercent = (bucket.total / maxBucketTotal) * 100;
                      const monthlyHeight = bucket.total > 0 ? (bucket.monthlyRevenue / bucket.total) * heightPercent : 0;
                      const yearlyHeight = bucket.total > 0 ? (bucket.yearlyRevenue / bucket.total) * heightPercent : 0;
                      const isPeak = bucket.monthIndex === forecast.peakBucket.monthIndex && bucket.total > 0;
                      const isPartial = bucket.monthIndex === 0 || bucket.monthIndex === FORECAST_BUCKETS - 1;
                      const isSelected = selectedForecastBucket === idx;

                      const tooltip = `${bucket.label}${isPartial ? ' (partial)' : ''}: ${formatCurrency(bucket.total)}`
                        + ` — Monthly: ${formatCurrency(bucket.monthlyRevenue)} (${bucket.monthlyCount} renewals)`
                        + ` · Yearly: ${formatCurrency(bucket.yearlyRevenue)} (${bucket.yearlyCount} renewals)`;

                      return (
                        <button
                          key={bucket.monthIndex}
                          type="button"
                          className={`${styles.chartBarGroup} ${isSelected ? styles.chartBarGroupSelected : ''}`}
                          onClick={() =>
                            setSelectedForecastBucket(isSelected ? null : idx)
                          }
                          aria-pressed={isSelected}
                          aria-label={tooltip}
                          title={tooltip}
                        >
                          <div className={styles.chartBarValue}>
                            {bucket.total > 0 ? formatCurrencyCompact(bucket.total) : ''}
                          </div>
                          <div className={styles.chartBarStack}>
                            {bucket.yearlyRevenue > 0 && (
                              <div
                                className={`${styles.chartBarSegment} ${styles.chartBarYearly}`}
                                style={{ height: `${yearlyHeight}%` }}
                              />
                            )}
                            {bucket.monthlyRevenue > 0 && (
                              <div
                                className={`${styles.chartBarSegment} ${styles.chartBarMonthly}`}
                                style={{ height: `${monthlyHeight}%` }}
                              />
                            )}
                          </div>
                          <div className={`${styles.chartBarLabel} ${isPeak ? styles.chartBarLabelPeak : ''} ${isPartial ? styles.chartBarLabelPartial : ''}`}>
                            {bucket.label}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {historical && (
            <>
              <div className={styles.forecastHeader}>
                <div className={styles.sectionHeaderRow}>
                  <h2 className={styles.sectionTitle}>New Subscriptions by Month</h2>
                  <div className={styles.timeToggle}>
                    <button
                      className={`${styles.toggleButton} ${historicalRange === '6mo' ? styles.toggleActive : ''}`}
                      onClick={() => setHistoricalRange('6mo')}
                    >
                      6mo
                    </button>
                    <button
                      className={`${styles.toggleButton} ${historicalRange === '12mo' ? styles.toggleActive : ''}`}
                      onClick={() => setHistoricalRange('12mo')}
                    >
                      12mo
                    </button>
                    <button
                      className={`${styles.toggleButton} ${historicalRange === '24mo' ? styles.toggleActive : ''}`}
                      onClick={() => setHistoricalRange('24mo')}
                    >
                      24mo
                    </button>
                    <button
                      className={`${styles.toggleButton} ${historicalRange === 'all' ? styles.toggleActive : ''}`}
                      onClick={() => setHistoricalRange('all')}
                    >
                      All
                    </button>
                  </div>
                </div>
                <p className={styles.forecastCaveat}>
                  Counts new <strong>recurring subscriptions only</strong> (Monthly +
                  Yearly Standard) by their <code>start_date</code>. Plan switches
                  (e.g. Monthly → Yearly) appear as one signup at the latest plan&apos;s
                  start date. Lifetime sales are shown in their own section below.
                </p>
              </div>

              <div className={styles.chartCard}>
                <div className={styles.chartLegend}>
                  <span className={styles.legendItem}>
                    <span className={styles.legendDot} style={{ background: '#3b82f6' }} />
                    Monthly Standard
                  </span>
                  <span className={styles.legendItem}>
                    <span className={styles.legendDot} style={{ background: '#22c55e' }} />
                    Yearly Standard
                  </span>
                  <span className={styles.legendNote}>
                    {historical.totalCounted.toLocaleString()} recurring signups in window
                    {' '}· avg {historical.avgPerMonth.toFixed(1)}/mo (months with activity)
                  </span>
                </div>

                <div
                  className={styles.chartReadout}
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {(() => {
                    if (selectedHistoricalBucket === null) {
                      return (
                        <span className={styles.chartReadoutHint}>
                          Tap a bar to see its breakdown
                        </span>
                      );
                    }
                    const b = historical.buckets[selectedHistoricalBucket];
                    if (!b) return null;
                    return (
                      <>
                        <strong>{b.label}:</strong>
                        {' '}{b.total} signup{b.total === 1 ? '' : 's'}
                        <span className={styles.chartReadoutBreakdown}>
                          {' — Monthly: '}{b.monthlyCount}
                          {' · Yearly: '}{b.yearlyCount}
                        </span>
                      </>
                    );
                  })()}
                </div>

                {historical.totalCounted === 0 ? (
                  <div className={styles.emptyChart}>
                    No recurring subscriptions started in the selected window.
                  </div>
                ) : (
                  <div
                    className={styles.chart}
                    onClick={(e) => {
                      if (e.target === e.currentTarget) setSelectedHistoricalBucket(null);
                    }}
                  >
                    {historical.buckets.map((bucket, idx) => {
                      const heightPercent = (bucket.total / maxHistoricalTotal) * 100;
                      const monthlyHeight = bucket.total > 0 ? (bucket.monthlyCount / bucket.total) * heightPercent : 0;
                      const yearlyHeight = bucket.total > 0 ? (bucket.yearlyCount / bucket.total) * heightPercent : 0;
                      const isPeak = bucket.monthIndex === historical.peakBucket.monthIndex && bucket.total > 0;
                      const isSelected = selectedHistoricalBucket === idx;

                      const tooltip = `${bucket.label}: ${bucket.total} signup${bucket.total === 1 ? '' : 's'}`
                        + ` — Monthly: ${bucket.monthlyCount}`
                        + ` · Yearly: ${bucket.yearlyCount}`;

                      // Show every label for ≤12 months, every 2nd for ≤24, else
                      // only first/middle/last to keep the X-axis legible.
                      const total = historical.buckets.length;
                      const showLabel = total <= 12
                        ? true
                        : total <= 24
                          ? bucket.monthIndex % 2 === total % 2
                          : bucket.monthIndex === 0
                              || bucket.monthIndex === total - 1
                              || bucket.monthIndex === Math.floor(total / 2);

                      return (
                        <button
                          key={bucket.monthIndex}
                          type="button"
                          className={`${styles.chartBarGroup} ${isSelected ? styles.chartBarGroupSelected : ''}`}
                          onClick={() =>
                            setSelectedHistoricalBucket(isSelected ? null : idx)
                          }
                          aria-pressed={isSelected}
                          aria-label={tooltip}
                          title={tooltip}
                        >
                          <div className={styles.chartBarValue}>
                            {bucket.total > 0 ? bucket.total : ''}
                          </div>
                          <div className={styles.chartBarStack}>
                            {bucket.yearlyCount > 0 && (
                              <div
                                className={`${styles.chartBarSegment} ${styles.chartBarYearly}`}
                                style={{ height: `${yearlyHeight}%` }}
                              />
                            )}
                            {bucket.monthlyCount > 0 && (
                              <div
                                className={`${styles.chartBarSegment} ${styles.chartBarMonthly}`}
                                style={{ height: `${monthlyHeight}%` }}
                              />
                            )}
                          </div>
                          <div className={`${styles.chartBarLabel} ${isPeak ? styles.chartBarLabelPeak : ''}`}>
                            {showLabel ? bucket.label : '\u00A0'}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {trend && (
            <>
              <div className={styles.forecastHeader}>
                <h2 className={styles.sectionTitle}>Active Subscriptions Over Time</h2>
                <p className={styles.forecastCaveat}>
                  End-of-month snapshots of <strong>recurring subscriptions only</strong>
                  (Monthly Standard + Yearly Standard), plus cancellations recorded in each
                  month. Range follows the toggle above. The most recent point is anchored to
                  live Stripe counts when available, so it matches the cards above. Earlier
                  points are reconstructed from each user&apos;s most-recent subscription record
                  and may underestimate slightly. Plan switches appear as a transition rather
                  than a separate cancellation. Lifetime is shown in its own section below.
                </p>
              </div>

              <div className={styles.grid}>
                <div className={`${styles.card} ${trend.netChange >= 0 ? styles.cardGreen : styles.cardRed}`}>
                  <div className={styles.cardLabel}>Net Change (Recurring)</div>
                  <div className={styles.cardValue}>
                    {trend.netChange >= 0 ? '+' : ''}{trend.netChange.toLocaleString()}
                  </div>
                </div>
                <div className={`${styles.card} ${styles.cardRed}`}>
                  <div className={styles.cardLabel}>Cancellations in Window</div>
                  <div className={styles.cardValue}>{trend.totalCanceledInWindow.toLocaleString()}</div>
                </div>
                <div className={`${styles.card} ${styles.cardGreen}`}>
                  <div className={styles.cardLabel}>Currently Active (Recurring)</div>
                  <div className={styles.cardValue}>
                    {(
                      trend.buckets[trend.buckets.length - 1].active.monthly +
                      trend.buckets[trend.buckets.length - 1].active.yearly
                    ).toLocaleString()}
                  </div>
                </div>
              </div>

              <div className={styles.chartCard}>
                <div className={styles.chartLegend}>
                  <span className={styles.legendItem}>
                    <span className={styles.legendLine} style={{ background: '#3b82f6' }} />
                    Monthly Standard
                  </span>
                  <span className={styles.legendItem}>
                    <span className={styles.legendLine} style={{ background: '#22c55e' }} />
                    Yearly Standard
                  </span>
                  <span className={styles.legendNote}>
                    Solid lines = active subs (left axis) · Bars = cancellations per month
                  </span>
                </div>

                {/* Line chart for active subs */}
                <SubscriptionLineChart trend={trend} />

                {/* Cancellation bars below */}
                <SubscriptionCancellationChart trend={trend} />
              </div>

              {/* === Lifetime Plan section (one-time product, separate metrics) === */}
              <div className={styles.forecastHeader}>
                <h2 className={styles.sectionTitle}>Lifetime Plan Over Time</h2>
                <p className={styles.forecastCaveat}>
                  Lifetime is a one-time purchase, so traditional churn / active-subs
                  metrics don&apos;t apply. The cumulative line shows total acquired
                  customers at the end of each month; the bars show new lifetime
                  purchases per month. Range follows the toggle above. New-purchase
                  counts are derived from month-over-month deltas in the cumulative
                  series, so the first bar in the window is always 0 (no prior
                  reference point).
                </p>
              </div>

              <div className={styles.grid}>
                <div className={`${styles.card} ${styles.cardPurple}`}>
                  <div className={styles.cardLabel}>Total Lifetime Customers</div>
                  <div className={styles.cardValue}>
                    {trend.buckets[trend.buckets.length - 1].active.lifetime.toLocaleString()}
                  </div>
                </div>
                <div className={styles.card}>
                  <div className={styles.cardLabel}>New in Window</div>
                  <div className={styles.cardValue}>
                    {trend.lifetimeNewInWindow.toLocaleString()}
                  </div>
                </div>
                <div className={styles.card}>
                  <div className={styles.cardLabel}>Avg / Month</div>
                  <div className={styles.cardValue}>
                    {trend.monthsBack > 0
                      ? (trend.lifetimeNewInWindow / trend.monthsBack).toFixed(1)
                      : '0'}
                  </div>
                </div>
                <div className={styles.card}>
                  <div className={styles.cardLabel}>Best Month</div>
                  <div className={styles.cardValue}>
                    {trend.lifetimeBestMonth && trend.lifetimeBestMonth.count > 0
                      ? trend.lifetimeBestMonth.count.toLocaleString()
                      : '—'}
                  </div>
                  {trend.lifetimeBestMonth && trend.lifetimeBestMonth.count > 0 && (
                    <div className={styles.cardSubNote}>{trend.lifetimeBestMonth.label}</div>
                  )}
                </div>
              </div>

              {trend.lifetimeRefundsInWindow > 0 && (
                <p className={styles.sourceFallbackNote}>
                  {trend.lifetimeRefundsInWindow.toLocaleString()} lifetime
                  {trend.lifetimeRefundsInWindow === 1 ? ' refund' : ' refunds'} recorded
                  in this window (subscriptions marked canceled in the local DB).
                </p>
              )}

              <div className={styles.chartCard}>
                <div className={styles.chartLegend}>
                  <span className={styles.legendItem}>
                    <span className={styles.legendLine} style={{ background: PLAN_COLORS.lifetime }} />
                    Cumulative customers
                  </span>
                  <span className={styles.legendItem}>
                    <span
                      className={styles.legendDot}
                      style={{ background: PLAN_COLORS.lifetime, opacity: 0.7 }}
                    />
                    New purchases / month
                  </span>
                  <span className={styles.legendNote}>
                    Solid line = total acquired (left axis) · Bars = new in month
                  </span>
                </div>

                <LifetimeCumulativeChart trend={trend} />
                <LifetimeNewPurchasesChart trend={trend} />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================================
// SVG sub-charts for the trend section. Inline because they share state
// closely with the parent's `trend` data and are not reused elsewhere.
// ============================================================================

const PLAN_COLORS = {
  monthly: '#3b82f6',
  yearly: '#22c55e',
  lifetime: '#a855f7',
} as const;

interface TrendBucket {
  monthIndex: number;
  monthStart: Date;
  monthEnd: Date;
  snapshotDate: Date;
  label: string;
  active: { monthly: number; yearly: number; lifetime: number };
  canceled: { monthly: number; yearly: number; lifetime: number };
}

interface TrendData {
  buckets: TrendBucket[];
  monthsBack: number;
  // Recurring-plan stats (used by the subscription line + cancellation charts).
  maxActive: number;
  maxCanceled: number;
  totalCanceledInWindow: number;
  netChange: number;
  // Lifetime-product stats (used by the dedicated Lifetime Plan section).
  lifetimeMaxActive: number;
  lifetimeNewByBucket: number[];
  lifetimeMaxNew: number;
  lifetimeNewInWindow: number;
  lifetimeRefundsInWindow: number;
  lifetimeBestMonth: { label: string; count: number } | null;
}

// Pretty Y-axis tick generator — finds a "nice" max and 4 evenly spaced ticks.
function niceTicks(maxValue: number): number[] {
  if (maxValue <= 1) return [0, 1];
  const exp = Math.floor(Math.log10(maxValue));
  const fraction = maxValue / Math.pow(10, exp);
  let niceFraction: number;
  if (fraction <= 1) niceFraction = 1;
  else if (fraction <= 2) niceFraction = 2;
  else if (fraction <= 5) niceFraction = 5;
  else niceFraction = 10;
  const niceMax = niceFraction * Math.pow(10, exp);
  return [0, niceMax * 0.25, niceMax * 0.5, niceMax * 0.75, niceMax];
}

function SubscriptionLineChart({ trend }: { trend: TrendData }) {
  // SVG viewBox uses logical 1000×280 coords; CSS scales to container.
  const VB_W = 1000;
  const VB_H = 280;
  const PAD_LEFT = 50;
  const PAD_RIGHT = 16;
  const PAD_TOP = 16;
  const PAD_BOTTOM = 28;
  const plotW = VB_W - PAD_LEFT - PAD_RIGHT;
  const plotH = VB_H - PAD_TOP - PAD_BOTTOM;

  const ticks = niceTicks(trend.maxActive);
  const yMax = ticks[ticks.length - 1];

  const n = trend.buckets.length;
  const xFor = (i: number) => PAD_LEFT + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const yFor = (v: number) => PAD_TOP + plotH - (v / yMax) * plotH;

  const buildPath = (key: 'monthly' | 'yearly' | 'lifetime') =>
    trend.buckets
      .map((b, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(2)} ${yFor(b.active[key]).toFixed(2)}`)
      .join(' ');

  // X-axis label sampling — show every label for ≤12 months, every Nth otherwise.
  const labelStride = n <= 12 ? 1 : n <= 24 ? 2 : Math.ceil(n / 8);

  return (
    <div className={styles.lineChartWrap}>
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio="none" className={styles.lineChart}>
        {/* Y-axis grid lines */}
        {ticks.map((t) => (
          <line
            key={t}
            x1={PAD_LEFT}
            y1={yFor(t)}
            x2={VB_W - PAD_RIGHT}
            y2={yFor(t)}
            className={styles.gridLine}
            vectorEffect="non-scaling-stroke"
          />
        ))}

        {/* Lines (recurring plans only — lifetime has its own section) */}
        <path
          d={buildPath('yearly')}
          stroke={PLAN_COLORS.yearly}
          fill="none"
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <path
          d={buildPath('monthly')}
          stroke={PLAN_COLORS.monthly}
          fill="none"
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* Data point dots with native tooltips */}
        {trend.buckets.map((b, i) => (
          <g key={b.monthIndex}>
            <circle
              cx={xFor(i)}
              cy={yFor(b.active.monthly)}
              r={3}
              fill={PLAN_COLORS.monthly}
              vectorEffect="non-scaling-stroke"
            >
              <title>{`${b.label} — Monthly Standard: ${b.active.monthly}`}</title>
            </circle>
            <circle
              cx={xFor(i)}
              cy={yFor(b.active.yearly)}
              r={3}
              fill={PLAN_COLORS.yearly}
              vectorEffect="non-scaling-stroke"
            >
              <title>{`${b.label} — Yearly Standard: ${b.active.yearly}`}</title>
            </circle>
          </g>
        ))}

        {/* X-axis labels */}
        {trend.buckets.map((b, i) => {
          if (i % labelStride !== 0 && i !== n - 1) return null;
          return (
            <text
              key={b.monthIndex}
              x={xFor(i)}
              y={VB_H - 8}
              textAnchor="middle"
              className={styles.axisLabel}
            >
              {b.label}
            </text>
          );
        })}

        {/* Y-axis labels */}
        {ticks.map((t) => (
          <text
            key={t}
            x={PAD_LEFT - 8}
            y={yFor(t) + 4}
            textAnchor="end"
            className={styles.axisLabel}
          >
            {Math.round(t).toLocaleString()}
          </text>
        ))}
      </svg>
    </div>
  );
}

function SubscriptionCancellationChart({ trend }: { trend: TrendData }) {
  const VB_W = 1000;
  const VB_H = 120;
  const PAD_LEFT = 50;
  const PAD_RIGHT = 16;
  const PAD_TOP = 16;
  const PAD_BOTTOM = 28;
  const plotW = VB_W - PAD_LEFT - PAD_RIGHT;
  const plotH = VB_H - PAD_TOP - PAD_BOTTOM;

  const ticks = niceTicks(trend.maxCanceled);
  const yMax = ticks[ticks.length - 1];

  const n = trend.buckets.length;
  // Bar centers; bar width = 60% of slot.
  const slotW = plotW / n;
  const barW = Math.max(slotW * 0.6, 2);

  const yFor = (v: number) => PAD_TOP + plotH - (v / yMax) * plotH;
  const xCenter = (i: number) => PAD_LEFT + slotW * (i + 0.5);

  const labelStride = n <= 12 ? 1 : n <= 24 ? 2 : Math.ceil(n / 8);

  return (
    <div className={styles.cancelChartWrap}>
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio="none" className={styles.cancelChart}>
        {/* Grid */}
        {ticks.map((t) => (
          <line
            key={t}
            x1={PAD_LEFT}
            y1={yFor(t)}
            x2={VB_W - PAD_RIGHT}
            y2={yFor(t)}
            className={styles.gridLine}
            vectorEffect="non-scaling-stroke"
          />
        ))}

        {/* Stacked bars (recurring plans only — lifetime has its own section) */}
        {trend.buckets.map((b, i) => {
          const total = b.canceled.monthly + b.canceled.yearly;
          if (total === 0) return null;
          const xL = xCenter(i) - barW / 2;
          const yBase = yFor(0);
          const monthlyH = (b.canceled.monthly / yMax) * plotH;
          const yearlyH = (b.canceled.yearly / yMax) * plotH;

          let cursor = yBase;
          const segs: ReactNode[] = [];
          if (b.canceled.monthly > 0) {
            cursor -= monthlyH;
            segs.push(
              <rect
                key="m"
                x={xL}
                y={cursor}
                width={barW}
                height={monthlyH}
                fill={PLAN_COLORS.monthly}
              >
                <title>{`${b.label}: ${b.canceled.monthly} Monthly cancellation${b.canceled.monthly === 1 ? '' : 's'}`}</title>
              </rect>,
            );
          }
          if (b.canceled.yearly > 0) {
            cursor -= yearlyH;
            segs.push(
              <rect
                key="y"
                x={xL}
                y={cursor}
                width={barW}
                height={yearlyH}
                fill={PLAN_COLORS.yearly}
              >
                <title>{`${b.label}: ${b.canceled.yearly} Yearly cancellation${b.canceled.yearly === 1 ? '' : 's'}`}</title>
              </rect>,
            );
          }
          return <g key={b.monthIndex}>{segs}</g>;
        })}

        {/* X-axis labels */}
        {trend.buckets.map((b, i) => {
          if (i % labelStride !== 0 && i !== n - 1) return null;
          return (
            <text
              key={b.monthIndex}
              x={xCenter(i)}
              y={VB_H - 8}
              textAnchor="middle"
              className={styles.axisLabel}
            >
              {b.label}
            </text>
          );
        })}

        {/* Y-axis labels */}
        {ticks.map((t) => (
          <text
            key={t}
            x={PAD_LEFT - 8}
            y={yFor(t) + 4}
            textAnchor="end"
            className={styles.axisLabel}
          >
            {Math.round(t).toLocaleString()}
          </text>
        ))}
      </svg>
      <div className={styles.cancelChartLabel}>Cancellations per month</div>
    </div>
  );
}

// ============================================================================
// Lifetime-specific charts. Cumulative line for total acquired customers and
// per-month bars for new purchases. Shape matches the recurring charts so the
// page reads consistently top-to-bottom.
// ============================================================================

function LifetimeCumulativeChart({ trend }: { trend: TrendData }) {
  const VB_W = 1000;
  const VB_H = 280;
  const PAD_LEFT = 50;
  const PAD_RIGHT = 16;
  const PAD_TOP = 16;
  const PAD_BOTTOM = 28;
  const plotW = VB_W - PAD_LEFT - PAD_RIGHT;
  const plotH = VB_H - PAD_TOP - PAD_BOTTOM;

  const ticks = niceTicks(trend.lifetimeMaxActive);
  const yMax = ticks[ticks.length - 1];

  const n = trend.buckets.length;
  const xFor = (i: number) => PAD_LEFT + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const yFor = (v: number) => PAD_TOP + plotH - (v / yMax) * plotH;

  const path = trend.buckets
    .map((b, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(2)} ${yFor(b.active.lifetime).toFixed(2)}`)
    .join(' ');

  const labelStride = n <= 12 ? 1 : n <= 24 ? 2 : Math.ceil(n / 8);

  return (
    <div className={styles.lineChartWrap}>
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio="none" className={styles.lineChart}>
        {ticks.map((t) => (
          <line
            key={t}
            x1={PAD_LEFT}
            y1={yFor(t)}
            x2={VB_W - PAD_RIGHT}
            y2={yFor(t)}
            className={styles.gridLine}
            vectorEffect="non-scaling-stroke"
          />
        ))}

        <path
          d={path}
          stroke={PLAN_COLORS.lifetime}
          fill="none"
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {trend.buckets.map((b, i) => (
          <circle
            key={b.monthIndex}
            cx={xFor(i)}
            cy={yFor(b.active.lifetime)}
            r={3}
            fill={PLAN_COLORS.lifetime}
            vectorEffect="non-scaling-stroke"
          >
            <title>{`${b.label} — ${b.active.lifetime.toLocaleString()} cumulative lifetime customers`}</title>
          </circle>
        ))}

        {trend.buckets.map((b, i) => {
          if (i % labelStride !== 0 && i !== n - 1) return null;
          return (
            <text
              key={b.monthIndex}
              x={xFor(i)}
              y={VB_H - 8}
              textAnchor="middle"
              className={styles.axisLabel}
            >
              {b.label}
            </text>
          );
        })}

        {ticks.map((t) => (
          <text
            key={t}
            x={PAD_LEFT - 8}
            y={yFor(t) + 4}
            textAnchor="end"
            className={styles.axisLabel}
          >
            {Math.round(t).toLocaleString()}
          </text>
        ))}
      </svg>
    </div>
  );
}

function LifetimeNewPurchasesChart({ trend }: { trend: TrendData }) {
  const VB_W = 1000;
  const VB_H = 120;
  const PAD_LEFT = 50;
  const PAD_RIGHT = 16;
  const PAD_TOP = 16;
  const PAD_BOTTOM = 28;
  const plotW = VB_W - PAD_LEFT - PAD_RIGHT;
  const plotH = VB_H - PAD_TOP - PAD_BOTTOM;

  const ticks = niceTicks(trend.lifetimeMaxNew);
  const yMax = ticks[ticks.length - 1];

  const n = trend.buckets.length;
  const slotW = plotW / n;
  const barW = Math.max(slotW * 0.6, 2);

  const yFor = (v: number) => PAD_TOP + plotH - (v / yMax) * plotH;
  const xCenter = (i: number) => PAD_LEFT + slotW * (i + 0.5);

  const labelStride = n <= 12 ? 1 : n <= 24 ? 2 : Math.ceil(n / 8);

  return (
    <div className={styles.cancelChartWrap}>
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} preserveAspectRatio="none" className={styles.cancelChart}>
        {ticks.map((t) => (
          <line
            key={t}
            x1={PAD_LEFT}
            y1={yFor(t)}
            x2={VB_W - PAD_RIGHT}
            y2={yFor(t)}
            className={styles.gridLine}
            vectorEffect="non-scaling-stroke"
          />
        ))}

        {trend.buckets.map((b, i) => {
          const newCount = trend.lifetimeNewByBucket[i];
          if (newCount === 0) return null;
          const xL = xCenter(i) - barW / 2;
          const yBase = yFor(0);
          const h = (newCount / yMax) * plotH;
          return (
            <rect
              key={b.monthIndex}
              x={xL}
              y={yBase - h}
              width={barW}
              height={h}
              fill={PLAN_COLORS.lifetime}
              opacity={0.85}
            >
              <title>{`${b.label}: ${newCount.toLocaleString()} new lifetime purchase${newCount === 1 ? '' : 's'}`}</title>
            </rect>
          );
        })}

        {trend.buckets.map((b, i) => {
          if (i % labelStride !== 0 && i !== n - 1) return null;
          return (
            <text
              key={b.monthIndex}
              x={xCenter(i)}
              y={VB_H - 8}
              textAnchor="middle"
              className={styles.axisLabel}
            >
              {b.label}
            </text>
          );
        })}

        {ticks.map((t) => (
          <text
            key={t}
            x={PAD_LEFT - 8}
            y={yFor(t) + 4}
            textAnchor="end"
            className={styles.axisLabel}
          >
            {Math.round(t).toLocaleString()}
          </text>
        ))}
      </svg>
      <div className={styles.cancelChartLabel}>New lifetime purchases per month</div>
    </div>
  );
}
